// User profile image cropping before uploading
import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop/types';
import debug from 'debug';

import getCroppedImg from './canvasUtils';

const log = debug('log:ImageCropTool');

function readFile(file: Blob) {
  return new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => resolve(reader.result?.toString() || null),
      false
    );
    reader.readAsDataURL(file);
  });
}

const ImageCropTool = ({ onCrop }: { onCrop: (image: string) => void }) => {
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);

        if (croppedImage) {
          onCrop(croppedImage);
        }
      } catch (e) {
        log(e);
      }
    })();
  }, [imageSrc, croppedAreaPixels]);

  const onCropComplete: Cropper['props']['onCropComplete'] = useCallback(
    (_croppedArea: Area, croppedAreaPx: Area) => {
      setCroppedAreaPixels(croppedAreaPx);
    },
    []
  );

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const imageDataUrl = await readFile(file);

      setImageSrc(imageDataUrl);
    }
  };

  return (
    <div>
      {imageSrc ? (
        <>
          <Cropper
            image={imageSrc || undefined}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </>
      ) : (
        <>
          <div>Select an image</div>
          <input type="file" onChange={onFileChange} accept="image/*" />
        </>
      )}
    </div>
  );
};

export default ImageCropTool;
